import { useContext } from 'react';
import ChatContext from 'contexts/Chat.context';
import { DISPATCH } from 'consts/chat.consts';
import useInfrastructure from '../useInfrastructure.hook';
import { v4 as uuidv4 } from 'uuid';
import ChatService from 'services/chat.service';
import { emit } from "infrastructure/eventHub";

const useChat = () => {
  const { repositories } = useInfrastructure();
  const { state, dispatch, selected } = useContext(ChatContext);
  const infrastructure = useInfrastructure();

  /**
   * This callback type is called `eventCallback` and is displayed as a global symbol.
   *
   * @callback dispatchCallback
   * @param {DISPATCH} TYPE
   */

  /**
   * Does something asynchronously and executes the callback on completion.
   * @param {dispatchCallback} dispatchCallback - The callback that handles the response.
   */
  const customDispatch = (dispatchCallback) => {
    if (typeof dispatchCallback === "function") {
      const { type, payload } = dispatchCallback(DISPATCH);
      if (typeof type === "string") {
        return dispatch({ type, payload });
      }
      return null;
    }
  };

  const reassignedConversation = async (areaId, agentId, reason) => {
    const conversation = selected.conversation;
    
    // Por defecto se asume que la conversación se cierra correctamente para mejorar la experiencia de usuario
    emit("chat", "conversation", "close", "local", {
      conversation
    });

    agentId = agentId === -1 ? undefined : agentId;
    const response = await ChatService.chatReassignment({
      lineId: conversation.lineId,
      chatId: conversation.chatId,
      areaId,
      agentId,
      reason
    });

    // En caso de que la respuesta del servidor sea un error, se revierte el borrado de la conversación para evitar inconsistencias en la UI
    if (!response.success) {
      repositories.conversations.upsert(conversation);
    }
    
    return { success: response.success };
  }

  const finishConversation = async (comment) => {
    const conversation = selected.conversation;

    // Por defecto se asume que la conversación se cierra correctamente para mejorar la experiencia de usuario
    emit("chat", "conversation", "close", "local", {
      conversation
    });

    const response = await ChatService.finishConversation({
      lineId: conversation.lineId,
      chatId: conversation.chatId,
      comment
    });

    // En caso de que la respuesta del servidor sea un error, se revierte el borrado de la conversación para evitar inconsistencias en la UI
    if (!response.success) {
      repositories.conversations.upsert(conversation);
    }

    return { success: response.success };
  }

  const sendMessage = async (content, type = 'text') => {
    const eventsRepository = repositories.events;
    const message = {
      from: selected.line.id,
      toWhatsAppId: selected.conversation.chatId,
      type: type.toUpperCase(),
      [type]: content
    };
    if (message.type === 'TEXT') {
      message.text = message.text?.trim();
      message.tempId = uuidv4();
      eventsRepository.upsert({
        id: message.tempId,
        tempId: message.tempId,
        chatId: selected.conversation.chatId,
        isSender: true,
        lineId: selected.line.id,
        status: 'SENDING',
        text: message.text,
        type: 'TEXT',
        timestamp: new Date().toISOString()
      });
    }

    const response = await ChatService.sendMessage(message);
    if (response.success) {
      return response;
    } else {
      eventsRepository.replaceByKey({
        index: "tempId",
        value: message.tempId,
        newItem: {
          status: 'FAILED',
          failedAt: new Date(),
          errors: [
            {
              code: 9991,
              title: 'Servidor temporalmente no disponible',
              message: 'Interrupción temporal del servicio. Vuelve a intentarlo'
            }
          ],
        }
      });
    }
  }

  const updateContact = async (contact) => {
    const conversations = await repositories.conversations.getAll();
    if (Array.isArray(conversations)) {
      const conversationsWithSameContact = conversations.filter((conversation) => {
        return conversation.contact && conversation.contact.id === contact.id;
      });
      await Promise.allSettled(conversationsWithSameContact.map(async (conversation) => {
        await repositories.conversations.patch(conversation.id, { contact });
      }));
    }
  }

  const sendNewComment = async (comment) => {
    const response = await ChatService.addComment({
      lineId: selected.line.id,
      chatId: selected.conversation.chatId,
      comment
    });
    return { success: response.success };
  }

  const readMessagesInConversation = ({ conversation }) => {
    infrastructure.socketManager.emit('messagesReadInConversation', { conversation });
    repositories.conversations.patch(conversation.id, { newMessages: 0 });
  }
  
  const closeConversation = () => {
    const conversation = selected.conversation;
    infrastructure.socketManager.emit('closeConversation', { conversation });
  }

  return {
    state,
    dispatch: customDispatch,
    selected,

    reassignedConversation,
    finishConversation,
    sendMessage,
    updateContact,
    sendNewComment,
    readMessagesInConversation,
    closeConversation,
  }
}

export default useChat;